import React, { useContext, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Skeleton,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Icon } from "@iconify/react";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
} from "recharts";
import { AuthContext } from "../../context/AuthContextProvider";
import { RequireAuth } from "../../RequireAuth";
import CardTitle from "./CardTitle";

const DashboardCasesBar = ({
  selectedDateFilter,
  casesGranularityState,
  setCasesGranularityState,
  loading,
  barData,
  isAllZero,
  CustomTooltip2,
  barsLoading,
  isLoadingTimeseries,
}) => {
  const { user } = useContext(AuthContext);

  // chart <-> table view toggle for the cases data
  const [view, setView] = useState("chart");

  const hasData = barData && barData.length > 0;

  // Export cases data as an Excel-openable workbook (no extra deps)
  const handleExportExcel = () => {
    if (!hasData) return;

    const headers = ["Period", "Cases"];
    const rows = barData.map((r) => [r.x, r.count ?? 0]);

    const headerHtml = headers
      .map(
        (h) =>
          `<th style="background:#F6F7F9;border:1px solid var(--app-border);padding:6px 10px;font-weight:700;text-align:left">${h}</th>`,
      )
      .join("");
    const bodyHtml = rows
      .map(
        (row) =>
          `<tr>${row
            .map(
              (c) =>
                `<td style="border:1px solid var(--app-border);padding:6px 10px">${c}</td>`,
            )
            .join("")}</tr>`,
      )
      .join("");

    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"></head><body><table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;

    const blob = new Blob([html], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Cases_Report.xls";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  // Reusable view toggle + export controls (shared by both layouts)
  const renderControls = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Button
        onClick={handleExportExcel}
        disabled={!hasData}
        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          fontSize: "14px",
          fontFamily: "Figtree",
          color: "#5751E9",
          borderRadius: "8px",
          px: 1.25,
          "&:hover": { backgroundColor: "var(--app-hover)" },
          "&.Mui-disabled": { color: "#B9BCC2" },
        }}
      >
        Export Excel
      </Button>

      <ToggleButtonGroup
        value={view}
        exclusive
        size="small"
        onChange={(e, val) => val && setView(val)}
        sx={{
          backgroundColor: "#F4F5F7",
          borderRadius: "10px",
          p: "3px",
          "& .MuiToggleButton-root": {
            border: "none",
            borderRadius: "8px !important",
            px: 1.25,
            py: 0.5,
            color: "var(--app-text-muted)",
            textTransform: "none",
            gap: "6px",
            "&.Mui-selected": {
              backgroundColor: "var(--app-surface)",
              color: "#5751E9",
              boxShadow: "var(--app-shadow)",
              "&:hover": { backgroundColor: "var(--app-surface)" },
            },
          },
        }}
      >
        <ToggleButton value="chart" aria-label="chart view">
          <BarChartRoundedIcon sx={{ fontSize: 18 }} />
        </ToggleButton>
        <ToggleButton value="table" aria-label="table view">
          <TableRowsRoundedIcon sx={{ fontSize: 18 }} />
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  // Table view of the same cases data shown in the bar chart
  const renderTable = () => (
    <TableContainer
      sx={{
        maxHeight: 320,
        borderRadius: "12px",
        border: "1px solid var(--app-border)",
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 700,
                color: "var(--app-text)",
                backgroundColor: "var(--app-surface-2)",
                fontFamily: "Figtree",
              }}
            >
              Period
            </TableCell>
            <TableCell
              align="center"
              sx={{
                fontWeight: 700,
                color: "var(--app-text)",
                backgroundColor: "var(--app-surface-2)",
                fontFamily: "Figtree",
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: "#5751E9",
                  }}
                />
                Cases
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {barData.map((row, idx) => (
            <TableRow key={`${row.x}-${idx}`} hover>
              <TableCell
                sx={{ fontWeight: 600, color: "var(--app-text)", fontFamily: "Figtree" }}
              >
                {row.x}
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 800, color: "var(--app-text)", fontFamily: "Figtree" }}
              >
                {row.count ?? 0}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      {user?.userType === "b2b-doctor" ? (
        <>
          <RequireAuth role={["b2b-doctor"]} permission={"canViewCases"}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <CardTitle>Cases</CardTitle>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: ".875rem",
                  fontWeight: 500,
                  color: "text.secondary",
                }}
              >
                {selectedDateFilter === "last_30_days" ||
                selectedDateFilter === "last_3_months" ? (
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={casesGranularityState.value}
                      onChange={(e) =>
                        setCasesGranularityState((prev) => ({
                          ...prev,
                          value: e.target.value,
                          label:
                            casesGranularityState.options.find(
                              (o) => o.value === e.target.value,
                            )?.label || prev.label,
                        }))
                      }
                      sx={{ fontSize: ".875rem", fontWeight: 500 }}
                    >
                      {casesGranularityState.options.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 500 ,"& .MuiTypography-root": {
                fontSize: "16px",
                color: "var(--app-text)",
                
              },}}>
                      {selectedDateFilter === "custom"
                        ? `Custom – ${casesGranularityState.label}`
                        : `${casesGranularityState.label}`}
                    </Typography>
                    <Icon
                      icon="mdi:chevron-down"
                      width="16"
                      className="text-gray-500"
                    />
                  </>
                )}
                {renderControls()}
              </Box>
            </Box>
            <Box
              className="col-md-12 pt-20"
              sx={{
                borderRight: "1px solid var(--app-border)",
                pl: 0, // paddingLeft: 0px
                pr: 0, // paddingRight: 0px
                pt: "20px", // equivalent to pt-20
              }}
            >
              <Box sx={{ px: { xs: 1.5, md: 0 } }}>
                {isLoadingTimeseries ? (
                  <Box className="flex items-center justify-center h-[300px] w-full">
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={300}
                      style={{ borderRadius: 12 }}
                    />
                  </Box>
                ) : barData && barData.length > 0 && !isAllZero ? (
                  view === "table" ? (
                    renderTable()
                  ) : (
                  <Box
                    sx={{
                      width: "100%",
                      overflowX: { xs: "auto", md: "hidden" },
                    }}
                  >
                    <Box sx={{ minWidth: { xs: 650, sm: 750, md: "100%" } }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={barData}
                          margin={{ top: 20, bottom: 20, left: -32, right: 20 }}
                        >
                          <CartesianGrid
                            stroke="var(--app-border)"
                            strokeDasharray="0"
                            vertical={false}
                          />
                          <XAxis
                            dataKey="x"
                            tick={{ fill: "var(--app-text)", fontSize: 13 , fontFamily: "Figtree", fontWeight: 700 }}
                            axisLine={{ stroke: "var(--app-border)" }}
                            tickLine={false}
                          />
                          <YAxis
                          tickFormatter={(value) => {
    if (Number.isInteger(value)) return value; // 5 → 5
    return Number(value).toFixed(1); // 0.23 → 0.2
  }}
                           tick={{ fill: "var(--app-text)", fontSize: 14 , fontFamily: "Figtree", fontWeight: 500 }}
                            axisLine={{ stroke: "none" }}
                            tickLine={false}
                          />
                          <Tooltip
                            cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
                            content={<CustomTooltip2 />}
                          />
                          <Bar
                            dataKey="count"
                            fill="#5751E9"
                            name="Cases"
                            barSize={36}
                            radius={[8, 8, 0, 0]}
                            animationDuration={800} activeBar={{ fillOpacity: 0.85, stroke: "#5751E9", strokeWidth: 1 }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                  )
                ) : (
                  <Box
                    sx={{
                      height: 300,
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={barsLoading}
                      alt="No data"
                      width={130}
                      height={130}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: 16,
                        color: "var(--app-text-muted)",
                        mt: 1,
                        fontFamily: "Figtree",
                      }}
                    >
                      No Cases Referred
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </RequireAuth>
        </>
      ) : (
        <>
          {/* Cases (Bar) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "8px",
              paddingLeft: 3,
            }}
          >
            <CardTitle>Cases</CardTitle>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75, // 6px (0.75 * 8px spacing unit)
              }}
            >
              {selectedDateFilter === "last_30_days" ||
              selectedDateFilter === "last_3_months" ? (
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={casesGranularityState.value}
                    onChange={(e) =>
                      setCasesGranularityState((prev) => ({
                        ...prev,
                        value: e.target.value,
                        label:
                          casesGranularityState.options.find(
                            (o) => o.value === e.target.value,
                          )?.label || prev.label,
                      }))
                    }
                    sx={{ fontSize: ".875rem", fontWeight: 500 }}
                  >
                    {casesGranularityState.options.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500 , "& .MuiTypography-root": {
                fontSize: "16px",
                color: "var(--app-text)",
                
              }, }}>
                    {selectedDateFilter === "custom"
                      ? `Custom – ${casesGranularityState.label}`
                      : `${casesGranularityState.label}`}
                  </Typography>
                  <Icon
                    icon="mdi:chevron-down"
                    width="16"
                    className="text-gray-500"
                  />
                </>
              )}
              {renderControls()}
            </Box>
          </Box>
          <Box
            className="col-md-12 pt-20"
            sx={{
              borderRight: "1px solid var(--app-border)",
              pl: 0, // paddingLeft: 0px
              pr: 0, // paddingRight: 0px
              pt: "20px", // equivalent to pt-20
            }}
          >
            <Box sx={{ px: { xs: 1.5, md: 0 } }}>
              {loading ? (
                <Box className="flex items-center justify-center h-[300px] w-full">
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={200}
                    style={{ borderRadius: 12 }}
                  />
                </Box>
              ) : barData && barData.length > 0 && !isAllZero ? (
                view === "table" ? (
                  renderTable()
                ) : (
                <Box
                  sx={{
                    width: "100%",
                    overflowX: { xs: "auto", md: "hidden" },
                  }}
                >
                  <Box sx={{ minWidth: { xs: 650, sm: 750, md: "100%" } }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={barData}
                        margin={{ top: 20, bottom: 20, left: -32, right: 20 }}
                      >
                        <CartesianGrid
                          stroke="var(--app-border)"
                          strokeDasharray="0"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="x"
                          tick={{ fill: "var(--app-text)", fontSize: 13 , fontFamily: "Figtree", fontWeight: 700 ,pt:10, }}
                          axisLine={{ stroke: "var(--app-border)" }}
                          tickLine={false}
                        />
                        <YAxis
                        tickFormatter={(value) => {
    if (Number.isInteger(value)) return value; // 5 → 5
    return Number(value).toFixed(1); // 0.23 → 0.2
  }}
                          tick={{ fill: "var(--app-text)", fontSize: 14 , fontFamily: "Figtree", fontWeight: 500 }}
                          axisLine={{ stroke: "none" }}
                          tickLine={false}
                        />
                        <Tooltip
                          cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
                          content={<CustomTooltip2 />}
                        />
                        <Bar
                          dataKey="count"
                          fill="#5751E9"
                          name="Cases"
                          barSize={36}
                          radius={[8, 8, 0, 0]}
                          animationDuration={800} activeBar={{ fillOpacity: 0.85, stroke: "#5751E9", strokeWidth: 1 }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
                )
              ) : (
                <Box
                  sx={{
                    height: 300,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={barsLoading}
                    alt="No data"
                    width={130}
                    height={130}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: 16,
                      color: "var(--app-text-muted)",
                      mt: 1,
                      fontFamily: "Figtree",
                    }}
                  >
                    No Cases Referred
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}
    </>
  );
};
export default DashboardCasesBar;
